import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Actions, Card, Table, useNotify, Loader, Space, Icon, Button } from 'ebs-design';
import { Pagination } from 'components/organisms';
import { ConfirmModal } from 'components/molecules';
import { useQueryParams, useQueryUpdate } from 'hooks/useQueryParams';
import { Plus } from 'resources/icons';
import { FormModal } from '../FormModal';
import { FormNomenclaturesCurrencies } from '../FormNomenclatures';
import { Results, Column, FilterType, Nomenclatures } from 'types';
import { useCurrenciesAPI } from 'api';
import { defaultFilters } from 'utils';
import { useRolePermission } from 'hooks/useRolePermission';
import { UserRoleCRM } from 'hooks/useFetcher/interfaces';

const formId = 'currencies';

export const NomenclaturesCurrencies: React.FC = () => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { updateQuery } = useQueryUpdate();
  const { getCurrencies, getCurrencyById, createCurrency, updateCurrency, deleteCurrency } =
    useCurrenciesAPI();

  const [isCreate, setIsCreate] = React.useState<boolean>(false);
  const [editItem, setEditItem] = React.useState();
  const [deleteItem, setDeleteItem] = React.useState();

  const hasEditPermission = useRolePermission([UserRoleCRM.admin]);

  const { data: currencies, isLoading } = useQuery<Results<Nomenclatures>>(
    ['currencies', { ...defaultFilters, ...params }],
    getCurrencies,
    {
      onError: () => {
        notify.error({ title: t('error.someThingIsWrong') });
      },
    },
  );

  const deleteCurrenciesMutation = useMutation(deleteCurrency, {
    onError: () => notify.error({ title: t('error.someThingIsWrong') }),
    onSuccess: () => {
      queryClient.invalidateQueries(['currencies']);
      setDeleteItem(undefined);
      notify.success({ title: t('success.successDataDelete') });
    },
  });

  const handleDelete = (): void => {
    if (deleteItem) {
      deleteCurrenciesMutation.mutate(deleteItem);
    }
  };

  const handleCancelDelete = (): void => {
    setDeleteItem(undefined);
  };

  const handleCloseModal = (): void => {
    setEditItem(undefined);
    setIsCreate(false);
  };

  const handleFilter = (type: FilterType, field: string): FilterType => {
    updateQuery({ ...params, ordering: type ? `${type === 'desc' ? '-' : ''}${field}` : type });
    return type;
  };

  const columns: Column<Nomenclatures>[] = React.useMemo(
    () => [
      {
        title: t('nomenclature.currencies'),
        onFilter: (type) => handleFilter(type, 'name'),
        dataIndex: 'name',
      },
      {
        title: t('nomenclature.symbol'),
        dataIndex: 'symbol',
      },
      {
        title: t('nomenclature.ratio'),
        dataIndex: 'ratio',
      },
      ...(hasEditPermission
        ? [
            {
              align: 'right',
              render: ({ id }) => (
                <Space justify="end">
                  <Actions placement="bottom">
                    <Actions.Item onClick={() => setEditItem(id)}>
                      {t('nomenclature.edit')}
                    </Actions.Item>
                    <Actions.Item onClick={() => setDeleteItem(id)}>
                      {t('nomenclature.delete')}
                    </Actions.Item>
                  </Actions>
                </Space>
              ),
            } as Column<Nomenclatures>,
          ]
        : []),
    ],
    [t, currencies, params],
  );

  return (
    <>
      <Space justify="space-between" className="mb-20">
        <h3 className="title">{`${t(`nomenclature.currencies`)}${
          currencies?.count ? ` (${currencies.count})` : ``
        }`}</h3>

        {hasEditPermission && (
          <Button
            type="primary"
            prefix={<Icon component={Plus} />}
            onClick={() => setIsCreate(true)}
          >
            {t('buttons.addNew')}
          </Button>
        )}
      </Space>

      <Card>
        <Loader loading={isLoading} size="regular">
          <Table
            emptyText={t('main.noData')}
            data={currencies?.results}
            columns={columns}
            className="no-border"
          />
        </Loader>
        <Card.Footer>
          <Pagination size={currencies?.total_pages || 0} />
        </Card.Footer>
      </Card>

      {!!deleteItem && <ConfirmModal onSuccess={handleDelete} onCancel={handleCancelDelete} />}

      {(editItem || isCreate) && (
        <FormModal formId={formId} onCancel={handleCloseModal} isCreate={isCreate}>
          <FormNomenclaturesCurrencies
            formId={formId}
            getItem={getCurrencyById}
            onFinish={isCreate ? createCurrency : updateCurrency}
            onClose={handleCloseModal}
            id={editItem}
          />
        </FormModal>
      )}
    </>
  );
};
